import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Store, Mail, BadgeCheck, Clock, ImagePlus, Loader2, Camera, FileText, User, Phone } from 'lucide-react';
import LocationPicker from './LocationPicker';
import PhotoGalleryUploader from './PhotoGalleryUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: 'lunedi', label: 'Lunedì' },
  { key: 'martedi', label: 'Martedì' },
  { key: 'mercoledi', label: 'Mercoledì' },
  { key: 'giovedi', label: 'Giovedì' },
  { key: 'venerdi', label: 'Venerdì' },
  { key: 'sabato', label: 'Sabato' },
  { key: 'domenica', label: 'Domenica' },
];

/** Geocode "provincia + città + indirizzo" via OpenStreetMap when GPS coords are missing.
 *  Tries structured (street+city+county) first; falls back to free-text including
 *  the province, then to free-text city only — so the shop ALWAYS gets a position
 *  as long as the city exists.
 */
async function geocodeAddress(indirizzo, citta, provincia) {
  const streetTrim = (indirizzo || '').trim();
  const cityTrim = (citta || '').trim();
  const provinceTrim = (provincia || '').trim();
  if (!streetTrim && !cityTrim) return null;
  const tryFetch = async (url) => {
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data && data[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    } catch (e) {
      console.warn('Geocoding fetch failed:', e);
    }
    return null;
  };
  // 1) Structured query (most precise). Use `county` for the province.
  if (streetTrim && cityTrim) {
    let url = `https://nominatim.openstreetmap.org/search?format=json&street=${encodeURIComponent(streetTrim)}&city=${encodeURIComponent(cityTrim)}&country=Italia&limit=1`;
    if (provinceTrim) url += `&county=${encodeURIComponent(provinceTrim)}`;
    const c = await tryFetch(url);
    if (c) return c;
  }
  // 2) Free-text "street, city, province, Italia".
  const parts = [streetTrim, cityTrim, provinceTrim, 'Italia'].filter(Boolean);
  const c2 = await tryFetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(parts.join(', '))}`);
  if (c2) return c2;
  // 3) City + province only.
  if (cityTrim) {
    const cityQ = provinceTrim ? `${cityTrim}, ${provinceTrim}, Italia` : `${cityTrim}, Italia`;
    return await tryFetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(cityQ)}`);
  }
  return null;
}

export default function ShopSetup({ shop, onShopCreated, onLoginRequested, isEdit = false, mode = 'full' }) {
  // mode: 'full' (initial setup), 'settings' (logo/photos/desc/hours/closure),
  //       'anagrafica' (only registry data from user menu),
  //       'signup-pro' (atomic professional signup: user account + shop).
  const isSignupPro = mode === 'signup-pro';
  const [formData, setFormData] = useState({
    nome: '',
    proprietario_nome_cognome: '',
    partita_iva: '',
    email_aziendale: '',
    telefono: '',
    provincia: '',
    indirizzo: '',
    citta: '',
    lat: null,
    lng: null,
    logo: '',
    orari_apertura: {},
    gallery_photos: [],
    descrizione: '',
  });
  // signup-pro extras (kept outside formData so payload to /shops stays clean)
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (shop) {
      setFormData({
        nome: shop.nome || '',
        proprietario_nome_cognome: shop.proprietario_nome_cognome || '',
        partita_iva: shop.partita_iva || '',
        email_aziendale: shop.email_aziendale || '',
        telefono: shop.telefono || '',
        provincia: shop.provincia || '',
        indirizzo: shop.indirizzo || '',
        citta: shop.citta || '',
        lat: shop.lat || null,
        lng: shop.lng || null,
        logo: shop.logo || '',
        orari_apertura: shop.orari_apertura || {},
        gallery_photos: shop.gallery_photos || [],
        descrizione: shop.descrizione || '',
      });
    }
  }, [shop]);

  // Live geocoding (debounced 700ms): every time the user changes province, city
  // or street and they HAVEN'T pinned the map manually yet, recompute the pin
  // automatically so the negoziante sees a provisional position within seconds.
  const liveProvincia = formData.provincia;
  const liveCitta = formData.citta;
  const liveIndirizzo = formData.indirizzo;
  const userPinned = formData._userPinned;
  useEffect(() => {
    if (userPinned) return;
    if (!liveCitta?.trim() && !liveIndirizzo?.trim()) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const coords = await geocodeAddress(liveIndirizzo, liveCitta, liveProvincia);
      if (!cancelled && coords) {
        setFormData((prev) => (prev._userPinned ? prev : { ...prev, lat: coords.lat, lng: coords.lng }));
      }
    }, 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [liveProvincia, liveCitta, liveIndirizzo, userPinned]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHoursChange = (day, value) => {
    setFormData((prev) => ({
      ...prev,
      orari_apertura: { ...prev.orari_apertura, [day]: value },
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const logoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setFormData((prev) => ({ ...prev, logo: logoUrl }));
    } catch (err) {
      setError('Errore durante il caricamento del logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSendOtp = async () => {
    setVerifying(true);
    setError(null);
    try {
      await axios.post(`${API}/shops/send-otp`, {}, { withCredentials: true });
      setOtpSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore nell'invio del codice OTP");
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyOtp = async () => {
    setVerifying(true);
    setError(null);
    try {
      await axios.post(`${API}/shops/verify-otp`, { otp_code: otpCode }, { withCredentials: true });
      const response = await axios.get(`${API}/shops/my`, { withCredentials: true });
      onShopCreated(response.data);
      setOtpSent(false);
      setOtpCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice OTP non valido');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.nome || !formData.indirizzo || !formData.citta) {
      setError('Compila nome negozio, indirizzo e città');
      setLoading(false);
      return;
    }
    if (isSignupPro) {
      if (!signupEmail.trim() || !signupPassword) {
        setError('Inserisci email e password per creare il tuo account');
        setLoading(false);
        return;
      }
      if (signupPassword.length < 8) {
        setError('La password deve avere almeno 8 caratteri');
        setLoading(false);
        return;
      }
    }

    let payload = { ...formData };

    // Fallback: if GPS missing, geocode from address+city
    if (!payload.lat || !payload.lng) {
      const coords = await geocodeAddress(payload.indirizzo, payload.citta, payload.provincia);
      if (coords) {
        payload.lat = coords.lat;
        payload.lng = coords.lng;
      } else {
        setError("Impossibile determinare la posizione: imposta il pin sulla mappa.");
        setLoading(false);
        return;
      }
    }

    try {
      let response;
      if (isSignupPro) {
        // Atomic professional signup (user + shop).
        response = await axios.post(
          `${API}/auth/register-pro`,
          { email: signupEmail.trim(), password: signupPassword, name: payload.nome, shop: payload },
          { withCredentials: true },
        );
      } else if (isEdit && shop) {
        response = await axios.put(`${API}/shops/my`, payload, { withCredentials: true });
      } else {
        response = await axios.post(`${API}/shops`, payload, { withCredentials: true });
      }
      onShopCreated(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const showAnagrafica = mode === 'full' || mode === 'anagrafica' || isSignupPro;
  const showImpostazioni = mode === 'full' || mode === 'settings';

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="shop-setup">
      {!isEdit && (
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="font-outfit font-bold text-xl text-slate-800 mb-2">
            {isSignupPro ? 'Registra la tua attività' : 'Registra il tuo negozio'}
          </h2>
          <p className="text-slate-500 text-sm">
            {isSignupPro
              ? "L'account viene creato solo se completi la registrazione del negozio."
              : 'Completa i dati base per iniziare. Potrai aggiungere logo, foto e orari dalle impostazioni.'}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* ANAGRAFICA */}
        {showAnagrafica && (
          <>
            <div>
              <Label htmlFor="proprietario_nome_cognome" className="flex items-center gap-2">
                <User className="w-4 h-4" /> Nome e cognome proprietario
              </Label>
              <Input
                id="proprietario_nome_cognome"
                name="proprietario_nome_cognome"
                value={formData.proprietario_nome_cognome}
                onChange={handleChange}
                placeholder="es. Mario Rossi (opzionale)"
                className="mt-1 rounded-xl"
                data-testid="shop-owner-name-input"
              />
            </div>

            <div>
              <Label htmlFor="nome">Nome negozio *</Label>
              <Input
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                placeholder="es. Calzature Mario"
                className="mt-1 rounded-xl"
                data-testid="shop-name-input"
              />
            </div>

            <div className="p-4 bg-slate-50 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="email_aziendale" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email aziendale (opzionale)
                </Label>
                {shop?.email_verified && (
                  <span className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    <BadgeCheck className="w-3.5 h-3.5" /> Verificata
                  </span>
                )}
              </div>
              <Input
                id="email_aziendale"
                name="email_aziendale"
                type="email"
                value={formData.email_aziendale}
                onChange={handleChange}
                placeholder="info@tuonegozio.it"
                className="rounded-xl bg-white"
                data-testid="shop-email-input"
              />
              {isEdit && formData.email_aziendale && !shop?.email_verified && (
                <>
                  {!otpSent ? (
                    <Button type="button" variant="outline" onClick={handleSendOtp} disabled={verifying}
                      className="w-full rounded-xl" data-testid="send-otp-button">
                      {verifying ? 'Invio in corso...' : 'Verifica email'}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <Input type="text" value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                        placeholder="Inserisci codice OTP" className="rounded-xl bg-white" maxLength={6}
                        data-testid="otp-input" />
                      <Button type="button" onClick={handleVerifyOtp} disabled={verifying || otpCode.length !== 6}
                        className="w-full bg-green-600 hover:bg-green-700 rounded-xl" data-testid="verify-otp-button">
                        {verifying ? 'Verifica...' : 'Conferma codice'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div>
              <Label htmlFor="partita_iva">Partita IVA (opzionale)</Label>
              <Input id="partita_iva" name="partita_iva" value={formData.partita_iva} onChange={handleChange}
                placeholder="es. IT12345678901" className="mt-1 rounded-xl" data-testid="shop-vat-input" />
            </div>

            <div>
              <Label htmlFor="telefono" className="flex items-center gap-2">
                <Phone className="w-4 h-4" /> Telefono negozio (opzionale)
              </Label>
              <Input
                id="telefono"
                name="telefono"
                type="tel"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="es. +39 049 1234567"
                className="mt-1 rounded-xl"
                data-testid="shop-phone-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Numero usato dai clienti per chiamarti dai servizi che hanno attivato <em>&quot;Mostra numero di telefono&quot;</em>. Modificalo qui per aggiornarlo ovunque.
              </p>
            </div>

            <div>
              <Label htmlFor="provincia">Provincia</Label>
              <Input id="provincia" name="provincia" value={formData.provincia} onChange={handleChange}
                placeholder="es. Padova" className="mt-1 rounded-xl" data-testid="shop-province-input" />
            </div>

            <div>
              <Label htmlFor="citta">Città *</Label>
              <Input id="citta" name="citta" value={formData.citta} onChange={handleChange}
                placeholder="es. Conselve" className="mt-1 rounded-xl" data-testid="shop-city-input" />
            </div>

            <div>
              <Label htmlFor="indirizzo">Indirizzo *</Label>
              <Input id="indirizzo" name="indirizzo" value={formData.indirizzo} onChange={handleChange}
                placeholder="es. Via Roma, 123" className="mt-1 rounded-xl" data-testid="shop-address-input" />
            </div>

            <div>
              <Label>Posizione sulla mappa</Label>
              <p className="text-xs text-slate-500 mb-2">
                Se non disponibile, calcoleremo automaticamente la posizione da provincia + città + indirizzo. Per affinare il pin, premi <strong>&ldquo;Usa la mia posizione&rdquo;</strong> oppure trascinalo.
              </p>
              <LocationPicker lat={formData.lat} lng={formData.lng}
                onChange={(lat, lng) => setFormData((prev) => ({ ...prev, lat, lng, _userPinned: true }))} />
            </div>

            {/* Password block — only in signup-pro mode (atomic account+shop creation). */}
            {isSignupPro && (
              <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3" data-testid="signup-pro-credentials">
                <p className="text-sm font-semibold text-blue-900">Credenziali di accesso</p>
                <div>
                  <Label htmlFor="signup_email">Email *</Label>
                  <Input id="signup_email" type="email" autoComplete="email" required
                    value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)}
                    placeholder="tu@email.it" className="mt-1 rounded-xl bg-white"
                    data-testid="signup-email-input" />
                </div>
                <div>
                  <Label htmlFor="signup_password">Password *</Label>
                  <Input id="signup_password" type="password" autoComplete="new-password" required minLength={8}
                    value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)}
                    placeholder="Min. 8 caratteri" className="mt-1 rounded-xl bg-white"
                    data-testid="signup-password-input" />
                </div>
              </div>
            )}
          </>
        )}

        {/* IMPOSTAZIONI NEGOZIO */}
        {showImpostazioni && (
          <>
            {!showAnagrafica && (
              <div className="text-sm text-slate-500 mb-2 -mt-2">
                Impostazioni del negozio: logo, foto, descrizione e orari.
              </div>
            )}

            <div>
              <Label className="text-sm font-medium text-slate-700">Logo negozio</Label>
              <div className="mt-2 flex items-center gap-4">
                {formData.logo ? (
                  <img src={formData.logo} alt="Logo" className="w-16 h-16 rounded-xl object-cover" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Store className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  <Button type="button" variant="outline" className="rounded-xl" disabled={uploadingLogo} asChild>
                    <span>
                      {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                      {formData.logo ? 'Cambia logo' : 'Carica logo'}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Camera className="w-4 h-4" /> Foto del negozio (max 4)
              </Label>
              <div className="mt-2">
                <PhotoGalleryUploader photos={formData.gallery_photos}
                  onChange={(photos) => setFormData((prev) => ({ ...prev, gallery_photos: photos }))} />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <FileText className="w-4 h-4" /> Descrizione attività (opzionale)
              </Label>
              <textarea
                value={formData.descrizione}
                onChange={(e) => setFormData((prev) => ({ ...prev, descrizione: e.target.value.slice(0, 500) }))}
                placeholder="Es. Siamo una calzoleria storica del centro, attiva dal 1965..."
                rows={4}
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 resize-none"
                data-testid="shop-descrizione"
              />
              <p className="text-[11px] text-slate-400 mt-1 text-right">{formData.descrizione.length}/500</p>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl">
              <Label className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4" /> Orari di apertura
              </Label>
              <div className="space-y-2">
                {DAYS.map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="w-24 text-sm text-slate-600">{label}</span>
                    <Input value={formData.orari_apertura[key] || ''}
                      onChange={(e) => handleHoursChange(key, e.target.value)}
                      placeholder='es. 09:00-13:00,15:00-19:00'
                      className="flex-1 rounded-lg text-sm bg-white" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Formato: 09:00-13:00,15:00-19:00 oppure &ldquo;chiuso&rdquo;
              </p>
            </div>
          </>
        )}

        {error && (
          <div className="text-sm bg-red-50 p-3 rounded-xl border border-red-100 space-y-2" data-testid="shop-error-wrap">
            <p className="text-red-700" data-testid="shop-error">{error}</p>
            {isSignupPro && /già registrata|già usata|già esistente/i.test(error) && onLoginRequested && (
              <button
                type="button"
                onClick={onLoginRequested}
                className="inline-flex items-center gap-2 text-blue-700 font-semibold text-sm hover:underline"
                data-testid="go-to-login-cta"
              >
                Effettua il login →
              </button>
            )}
          </div>
        )}

        <Button type="submit" disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 rounded-xl py-3"
          data-testid="save-shop-button">
          {loading
            ? 'Salvataggio...'
            : isEdit
              ? 'Salva modifiche'
              : isSignupPro
                ? 'Crea account e negozio'
                : 'Registra negozio'}
        </Button>
      </form>
    </div>
  );
}
